import { fromDateOnly, convertToTimezone, convertToTimezoneNullable } from "@gigpro/utils/datetime";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { produce } from "immer";
import {
  adminCertificationRequestsPartialUpdate,
  getAdminGigsterProfilesSickTimeRetrieveQueryOptions,
  useAdminGigApplicationsList,
} from "../../generated/admin/admin";
import type { AdminCertificationRequest } from "../../generated/model/adminCertificationRequest";
import type { AdminGigApplication } from "../../generated/model/adminGigApplication";
import type { AdminProSickTime } from "../../generated/model/adminProSickTime";
import type { PaginatedAdminGigApplicationList } from "../../generated/model/paginatedAdminGigApplicationList";
import type { PatchedAdminCertificationRequestRequest } from "../../generated/model/patchedAdminCertificationRequestRequest";
import { composeSelect, type DateOnlyInputs, toDateOnlyFields } from "../types";

type AdminCertificationRequestData = DateOnlyInputs<PatchedAdminCertificationRequestRequest, "expiration_date">;
interface AdminCertificationRequestPartialUpdateWithTransformVariables {
  id: string;
  data?: AdminCertificationRequestData;
}

export function useAdminCertificationRequestsPartialUpdateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<
      AdminCertificationRequest,
      unknown,
      AdminCertificationRequestPartialUpdateWithTransformVariables
    >,
    "mutationFn"
  >;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: ({ id, data }: AdminCertificationRequestPartialUpdateWithTransformVariables) =>
      adminCertificationRequestsPartialUpdate(id, data ? toDateOnlyFields(data, ["expiration_date"]) : undefined),
  });
}

export const applyTzToApplication = produce<AdminGigApplication>((draft) => {
  const tz = draft.timezone;
  draft.gig_start_time = convertToTimezone(draft.gig_start_time, tz);
  if (draft.working_hours) {
    draft.working_hours.start_time = convertToTimezone(draft.working_hours.start_time, tz);
    draft.working_hours.end_time = convertToTimezone(draft.working_hours.end_time, tz);
    draft.working_hours.original_start_time = convertToTimezoneNullable(draft.working_hours.original_start_time, tz);
    draft.working_hours.original_end_time = convertToTimezoneNullable(draft.working_hours.original_end_time, tz);
  }
  if (draft.timesheet) {
    for (const punch of draft.timesheet.time_punches) {
      punch.time = convertToTimezone(punch.time, tz);
    }
    for (const breakWindow of [
      ...draft.timesheet.shift_breaks,
      ...draft.timesheet.break_claims,
      draft.timesheet.pro_break_resolution,
      draft.timesheet.business_break_resolution,
    ]) {
      breakWindow.start_time = convertToTimezoneNullable(breakWindow.start_time, tz);
      breakWindow.end_time = convertToTimezoneNullable(breakWindow.end_time, tz);
    }
  }
});

export function useAdminGigApplicationsListWithTransform<TData = PaginatedAdminGigApplicationList>(
  ...[params, options]: Parameters<typeof useAdminGigApplicationsList<TData>>
) {
  return useAdminGigApplicationsList<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(
        (data: PaginatedAdminGigApplicationList) => ({ ...data, results: data.results.map(applyTzToApplication) }),
        options?.query?.select,
      ),
    },
  });
}

// `rate_period_start` / `work_date` are date-only: keep their calendar day instead of UTC midnight.
const applyLocalDaysToSickTime = produce<AdminProSickTime>((draft) => {
  if (draft.summary?.rate_period_start) draft.summary.rate_period_start = fromDateOnly(draft.summary.rate_period_start);
  for (const row of draft.usage) row.work_date = fromDateOnly(row.work_date);
});

export const getAdminGigsterProfilesSickTimeRetrieveWithTransformQueryOptions = <TData = AdminProSickTime>(
  ...[userId, options]: Parameters<typeof getAdminGigsterProfilesSickTimeRetrieveQueryOptions<TData>>
) =>
  getAdminGigsterProfilesSickTimeRetrieveQueryOptions<TData>(userId, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(applyLocalDaysToSickTime, options?.query?.select),
    },
  });
